import { useState } from 'react';
import { useSuperMutation, useSuperQuery } from '@vadiun/react-hooks-legacy';
import { trafficLightRulesConfigurationService } from '../services/trafficLightRulesConfigurationService';
import { useParams } from 'react-router-dom';
import { RulesConfigurationFormType } from '../types/types';
import { useSnackbar } from 'notistack';

const mutationErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Ocurrió un error inesperado';
};

export const useTrafficLightRulesConfigurations = () => {
  const service = trafficLightRulesConfigurationService();
  const params = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const listQuery = useSuperQuery(
    () => service.getRulesConfigurations(page, rowsPerPage),
    { showSpinner: true },
    [page, rowsPerPage]
  );

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setPage(1);
    setRowsPerPage(newRowsPerPage);
  };

  const createMutation = useSuperMutation(
    (values: RulesConfigurationFormType) =>
      service.createSemaphoreRuleGroup(values),
    {
      onMutationFinish() {
        listQuery.reload();
      },
      onError(error: unknown) {
        enqueueSnackbar(mutationErrorMessage(error), { variant: 'error' });
      },
      messages: {
        success: { message: 'La configuración fue creada con éxito' },
      }
      // useSuperMutation reenvía opciones de React Query; los tipos legacy no incluyen onError.
    } as any
  );

  const updateMutation = useSuperMutation(
    (payload: { id: number; values: RulesConfigurationFormType }) =>
      service.updateSemaphoreRuleGroup(payload.id, payload.values),
    {
      onMutationFinish() {
        listQuery.reload();
      },
      onError(error: unknown) {
        enqueueSnackbar(mutationErrorMessage(error), { variant: 'error' });
      },
      messages: {
        success: { message: 'La regla fue actualizada con éxito' }
      }
    } as any
  );

  const deleteMutation = useSuperMutation(
    (id: number) => service.deleteSemaphoreRuleGroup(id),
    {
      onMutationFinish() {
        listQuery.reload();
      },
      onError(error: unknown) {
        enqueueSnackbar(mutationErrorMessage(error), { variant: 'error' });
      },
      messages: {
        success: { message: 'La regla fue eliminada con éxito' }
      }
    } as any
  );

  const detailQuery = useSuperQuery(
    () => {
      if (!params.id) return Promise.resolve(null);
      return service.getSemaphoreRuleGroupById(Number(params.id));
    },
    { showSpinner: true }
  );

  return {
    semaphoreRuleGroups: {
      data: listQuery.data?.docs || [],
      totalItems: listQuery.data?.totalDocs || 0,
      loading: listQuery.isLoading,
      error: listQuery.error,
      page,
      rowsPerPage,
      handleChangePage,
      handleChangeRowsPerPage,
      reload: listQuery.reload,
    },
    createMutation,
    updateMutation,
    deleteMutation,
    detailQuery
  };
};

